import React, { useState,useEffect, useCallback  } from "react";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete"; 
import Styles from "./CompanyDetails.module.css";
import DocumentUploaderModal from "./DocumentUploaderModal";
import { DOCUMENT_LIST, DOCUMENT_GROUPS_LIST } from "../../../commons/documentTypes";
import { useDispatch,useSelector } from "react-redux";
import { fetchCompanyById } from "../../../Redux/Slices/gnaAdminSlice";
import { getTraderMap, deleteTraderMap } from "../../../Services/Admin/Alter";
import { toast } from "react-toastify";
import EditIcon from "@mui/icons-material/Edit";


const DocumentDetails = ({ company, handlePreviewGeneralTerms, companyId, userAction }) => {
  
  const mapping = company?.companyDocumentMapping || {};
  const dispatch = useDispatch();


  // Add these three selectors back here:
  const traders = useSelector((state) => state?.gnaAdminSlice?.fetchFullTrader?.data) || [];
  const activeConsumers = useSelector((state) => state?.gnaAdminSlice?.fetchConsumersCompanyId?.data) || [];
  const activeGenerators = useSelector((state) => state?.gnaAdminSlice?.fetchGeneratorsCompanyId?.data) || [];
  const [nocRecords, setNocRecords] = useState([]);
  const [loadingNoc, setLoadingNoc] = useState(false);

  // Fetch assigned traders dynamically
  const fetchAssignedTraders = useCallback(async () => {
    if (!companyId) return;
    setLoadingNoc(true);
    try {
      const [consumerRes, generatorRes] = await Promise.all([
        getTraderMap(companyId, "CONSUMER"),
        getTraderMap(companyId, "GENERATOR"),
      ]);

      const consumersRes = consumerRes?.data?.code === 200 ? consumerRes.data.data || [] : [];
      const generatorsRes = generatorRes?.data?.code === 200 ? generatorRes.data.data || [] : [];

      // Filter out inactive/unverified/on-hold mappings using the active lists
      const activeConsumerIds = new Set(activeConsumers.map((c) => c.consumerId));
      const activeGeneratorIds = new Set(activeGenerators.map((g) => g.generatorId));

      const filteredConsumers = consumersRes.filter((m) => activeConsumerIds.has(m.entityId));
      const filteredGenerators = generatorsRes.filter((m) => activeGeneratorIds.has(m.entityId));

      const combined = [...filteredConsumers, ...filteredGenerators].map((item) => {
        // Resolve trader's name using their userId
        const traderObj = traders.find((tr) => tr.userId === item.userId);
        const traderName = traderObj?.fullName || item.traderName || "—";

        return {
          traderEntityMapId: item.id,
          name: item.entityName || "—",
          traderName: traderName,
          entityType: item.entityType,
          docId: item.docId,
          nocExpiryAt: item.nocExpiryAt,
        };
      });

      setNocRecords(combined);
    } catch (err) {
      console.error("Failed to fetch trader mappings:", err);
    }
    setLoadingNoc(false);
  }, [companyId, traders, activeConsumers, activeGenerators]);

  // Fetch when page loads or when company changes (e.g. after uploading replacement)
  useEffect(() => {
    fetchAssignedTraders();
  }, [fetchAssignedTraders, company]);

  // Delete/unassign mapping handler
  const handleDeleteNocMapping = async (mapId) => {
    if (!window.confirm("Are you sure you want to delete this trader mapping?")) return;
    try {
      const res = await deleteTraderMap(mapId);
      if (res?.data?.code === 200) {
        toast.success(res?.data?.message || "Trader mapping deleted successfully.");
        fetchAssignedTraders();
        dispatch(fetchCompanyById(companyId)); // Sync backend state to Redux
      } else {
        toast.error(res?.data?.error || "Failed to delete mapping.");
      }
    } catch (err) {
      toast.error("An error occurred while deleting.");
    }
  };

  const [modalState, setModalState] = useState({
    open: false,
    documentKey: "",
    documentLabel: "",
    isReplacement: false,
    traderEntityMapId: null,
  });

  const openUploadModal = (key, label, isReplacement = false) => {
    setModalState({
      open: true,
      documentKey: key,
      documentLabel: label,
      isReplacement,
      traderEntityMapId: null,
    });
  };

  const closeModal = () => {
    setModalState({ open: false, documentKey: "", documentLabel: "", isReplacement: false, traderEntityMapId: null });
  };

    return (
    <div className={Styles.docSection}>
      <DocumentUploaderModal
        open={modalState.open}
        onClose={closeModal}
        companyId={companyId}
        documentKey={modalState.documentKey}
        documentLabel={modalState.documentLabel}
        isReplacement={modalState.isReplacement}
        traderEntityMapId={modalState.traderEntityMapId}
      />

      <div className={Styles.docCard}>
        <div className={Styles.docCardTitle}>General Documents</div>
        <div className={Styles.docGrid}>
          {/* 1. Renders main grid documents (PAN Card, TAN, GST Certificate, etc.) */}
          {DOCUMENT_LIST.map(({ label, key }) => {
            const docId = mapping[key];
            const hasDocument = Boolean(docId);

            return (
              <div key={key} className={Styles.docTile}>
                <span className={Styles.docTileLabel}>{label}</span>

                {!hasDocument ? (
                  <div className={Styles.docTileEmptyRow}>
                    <span className={Styles.docTileEmpty}>No Document Uploaded</span>
                    {userAction && (
                      <button
                        className={Styles.docUploadBtn}
                        onClick={() => openUploadModal(key, label, false)}
                      >
                        <AddIcon style={{ fontSize: "14px" }} />
                        Upload
                      </button>
                    )}
                  </div>
                ) : (
                  <div className={Styles.docTileActions}>
                    <button
                      className={Styles.docViewBtn}
                      onClick={() => handlePreviewGeneralTerms(docId)}
                    >
                      <RemoveRedEyeIcon style={{ fontSize: "13px" }} />
                      View
                    </button>
                                        {userAction && (
                      <button
                        className={Styles.docReplaceBtn}
                        title={`Edit ${label}`}
                        onClick={() => openUploadModal(key, label, true)}
                      >
                        <EditIcon style={{ fontSize: "14px", marginRight: "4px" }} />
                        Edit
                      </button>
                    )}

                  </div>
                )}
              </div>
            );
          })}

          {/* 2. Renders group documents except NOC (e.g. Other Files) */}
          {DOCUMENT_GROUPS_LIST.filter(item => item.key !== "nocDocs").map(({ label, key }) => {
            const docList = Array.isArray(mapping[key]) ? mapping[key] : [];
            return (
              <div key={key} className={Styles.docTile}>
                <span className={Styles.docTileLabel}>{label}</span>
                {docList.length > 0 ? (
                  <div className={Styles.docChipRow}>
                    {docList.map((doc, docIdx) => {
                      const isViewable = doc?.docId && doc.docId !== 0;

                      return (
                        <button
                          key={docIdx}
                          type="button"
                          className={`${Styles.docChip} ${
                            isViewable ? Styles.docChipClickable : ""
                          }`}
                          onClick={
                            isViewable
                              ? () => handlePreviewGeneralTerms(doc.docId)
                              : undefined
                          }
                          disabled={!isViewable}
                        >
                          <span>{doc?.name || "NA"}</span>
                          {isViewable && (
                            <RemoveRedEyeIcon style={{ fontSize: "12px" }} />
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <span className={Styles.docTileEmpty}>No Document Uploaded</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 3. NOC Documents (Tabular Layout) */}
      <div className={Styles.docCard} style={{ marginTop: "24px" }}>
        <div className={Styles.docCardTitle}>NOC Documents</div>
        
        <div className={Styles.tableWrapper} style={{ marginTop: "16px" }}>
          {loadingNoc ? (
            <div className={Styles.emptyText}>Loading...</div>
          ) : nocRecords.length === 0 ? (
            <div className={Styles.emptyText}>No NOC Documents uploaded</div>
          ) : (
                        <table className={Styles.table}>
              <thead>
                <tr>
                  <th style={{ width: "80px" }}>#</th>
                  <th>Trader Name</th>
                  <th>Entity Name</th>
                  <th>Entity Type</th>
                  <th>Expiry Date</th>
                  <th style={{ width: "180px", textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {nocRecords.map((doc, index) => {
                  const isViewable = doc?.docId && doc.docId !== 0;
                  return (
                    <tr key={doc.traderEntityMapId || index}>
                      <td>{index + 1}</td>
                      <td>{doc?.traderName || "—"}</td>
                      <td>{doc?.name || "—"}</td>
                      <td>
                        <span className={`${Styles.badge} ${Styles[doc.entityType?.toLowerCase() || ""]}`}>
                          {doc.entityType || "—"}
                        </span>
                      </td>
                      <td>
                        {doc.nocExpiryAt 
                          ? (Array.isArray(doc.nocExpiryAt) 
                              ? `${doc.nocExpiryAt[0]}-${String(doc.nocExpiryAt[1]).padStart(2, '0')}-${String(doc.nocExpiryAt[2]).padStart(2, '0')}`
                              : String(doc.nocExpiryAt).substring(0, 10))
                          : "Not Set"
                        }
                      </td>
                      <td>
                        <div className={Styles.actionBtns} style={{ justifyContent: "center" }}>
                          <button
                            className={Styles.viewBtn}
                            title="View NOC Document"
                            onClick={isViewable ? () => handlePreviewGeneralTerms(doc.docId) : undefined}
                            disabled={!isViewable}
                          >
                            <RemoveRedEyeIcon style={{ fontSize: "14px" }} />
                          </button>
                          {userAction && (
                            <button
                            className={Styles.docReplaceBtn}
                              title={`Edit NOC for ${doc.name}`}
                              onClick={() => {
                                setModalState({
                                  open: true,
                                  documentKey: "nocDoc",
                                  documentLabel: `NOC for ${doc.name}`,
                                  isReplacement: true,
                                  traderEntityMapId: doc.traderEntityMapId,
                                });
                              }}
                            >
                              <EditIcon style={{ fontSize: "14px" }} />
                            </button>
                          )}
                          {userAction && (
                            <button
                              className={Styles.deleteBtn}
                              title="Delete Mapping"
                              onClick={() => handleDeleteNocMapping(doc.traderEntityMapId)}
                            >
                              <DeleteIcon style={{ fontSize: "14px" }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
export default DocumentDetails;
